import { KnowledgeOwnerType } from './knowledge';
import { MessagingPlatform } from './messagingPlatform.enum';
import { Priority } from './priority.enum';
import { TeamInviteStatus } from './team';

export enum NotificationDisplayType {
  BANNER = 'banner',
  NOTIFICATION_CENTER = 'notification_center',
}

export enum NotificationKind {
  TEAM_INVITATION_PENDING = 'TEAM_INVITATION_PENDING',
  DOBOX_GENERATED = 'DOBOX_GENERATED',
  DOBOX_ASSIGNED = 'DOBOX_ASSIGNED',
  TASK_GROUP_ADDED = 'TASK_GROUP_ADDED',
  DOBOX_COMMENT_MENTION = 'DOBOX_COMMENT_MENTION',
  KNOWLEDGE_PAGE_MENTION = 'KNOWLEDGE_PAGE_MENTION',
  SYNC_FAILED = 'SYNC_FAILED',
  GOOGLE_REVOKE_FAILED = 'GOOGLE_REVOKE_FAILED',
}

interface BaseNotification {
  id?: string;
  userId?: string;
  createdAt?: number;
  priority?: Priority;
  displayType?: NotificationDisplayType;
  wasRead?: boolean;
  message: string;
  redirectUrl?: string;
  updatedBy?: string;
  sourceService?: string;
  errorMessage?: string;
  /** Internal GSI partition key: `${userId}#${read|unread}`. Maintained by NotificationStore. */
  userReadStateKey?: string;
  /**
   * Set by the outcome of a sync run and cleared by the next run's first tick.
   * A progress write is conditional on its absence, so a tick still draining
   * from a run that already ended cannot revert the outcome to "still syncing"
   * — the queue is not FIFO and its handler fans a batch out concurrently, so
   * emission order guarantees nothing.
   */
  syncTerminalAt?: number;
}

export interface TeamInvitationPendingData {
  teamId: string;
  teamName: string;
  inviterUserId: string;
  inviterUserName: string;
  respondedAt?: number;
  outcome?: TeamInviteStatus;
}

export enum DoBoxNotificationResolvedBy {
  AGENT = 'agent',
}

export interface DoBoxGeneratedData {
  inboxEntryId: string;
  taskGroupId: string;
  subject: string;
  resolvedAt?: number;
  resolvedBy?: DoBoxNotificationResolvedBy;
}

export interface DoBoxAssignedData {
  inboxEntryId: string;
  taskGroupId: string;
  subject: string;
  assignedBy: string;
  resolvedAt?: number;
  resolvedBy?: DoBoxNotificationResolvedBy;
}

export interface TaskGroupAddedData {
  taskGroupId: string;
  taskGroupTitle: string;
  addedBy: string;
}

export interface DoBoxCommentMentionData {
  inboxEntryId: string;
  taskGroupId: string;
  commentId: string;
  subject: string;
  mentionedBy: string;
  mentionedByName: string;
}

export interface KnowledgePageMentionData {
  pageId: string;
  spaceId: string;
  ownerType: KnowledgeOwnerType;
  pageTitle: string;
  mentionedBy: string; // editor userId (actingUserId when an AGENT made the edit)
  mentionedByName: string;
  version: number; // the version that introduced the mention
}

interface TeamInvitationPendingNotification extends BaseNotification {
  kind: NotificationKind.TEAM_INVITATION_PENDING;
  data: TeamInvitationPendingData;
}

interface DoBoxGeneratedNotification extends BaseNotification {
  kind: NotificationKind.DOBOX_GENERATED;
  data: DoBoxGeneratedData;
}

interface DoBoxAssignedNotification extends BaseNotification {
  kind: NotificationKind.DOBOX_ASSIGNED;
  data: DoBoxAssignedData;
}

interface TaskGroupAddedNotification extends BaseNotification {
  kind: NotificationKind.TASK_GROUP_ADDED;
  data: TaskGroupAddedData;
}

interface DoBoxCommentMentionNotification extends BaseNotification {
  kind: NotificationKind.DOBOX_COMMENT_MENTION;
  data: DoBoxCommentMentionData;
}

interface KnowledgePageMentionNotification extends BaseNotification {
  kind: NotificationKind.KNOWLEDGE_PAGE_MENTION;
  data: KnowledgePageMentionData;
}

/**
 * Carries no user content: `platform` is an enum and `accountKey` is the opaque
 * key already in the notification's own id — for Gmail an `hmacEmail` digest,
 * never the mailbox address — so nothing here is lifted into the sealed set.
 */
export interface SyncFailedData {
  platform: MessagingPlatform;
  accountKey: string;
}

interface SyncFailedNotification extends BaseNotification {
  kind: NotificationKind.SYNC_FAILED;
  data: SyncFailedData;
}

/**
 * No `data`. The account this failed for is named in `message`, which seals under
 * `notification_content` — an `hmacEmail` digest was the alternative and it is write-only:
 * nothing reverses an HMAC, and unlike `SyncFailedData`'s it would address no row, because
 * this notification takes a fresh id per failure rather than a deterministic one.
 */
interface GoogleRevokeFailedNotification extends BaseNotification {
  kind: NotificationKind.GOOGLE_REVOKE_FAILED;
  // Declared absent, like LegacyNotification: `processCreateNotification` reads `.data` off
  // the union, so an arm that omits it entirely stops compiling there.
  data?: undefined;
}

interface LegacyNotification extends BaseNotification {
  kind?: undefined;
  data?: undefined;
}

export type Notification =
  | LegacyNotification
  | TeamInvitationPendingNotification
  | DoBoxGeneratedNotification
  | DoBoxAssignedNotification
  | DoBoxCommentMentionNotification
  | KnowledgePageMentionNotification
  | TaskGroupAddedNotification
  | SyncFailedNotification
  | GoogleRevokeFailedNotification;

export interface PaginatedNotifications {
  notifications: Notification[];
  exclusiveStartKey?: string | null;
}

export enum NotificationReadState {
  READ = 'read',
  UNREAD = 'unread',
}

export const READ_STATE_KEY_SEPARATOR = '#';
